import React from 'react';
import PropTypes from 'prop-types';

import Page from 'Common/Layout/Page';
import Section from 'Common/Layout/Section';
import TeamTypeFilter from 'Common/Components/TeamTypeFilter/TeamTypeFilter.container';
import DataTable, { dataSourceTypes } from 'Common/Table/DataTable';
import ExpansionContent from '../Components/ExpansionContent/ExpansionContent';
import Refiner from 'Common/Components/Refiner/Refiner.container';
import ExportCsvButton from 'Common/Buttons/ExportCsvButton';
import { getRefinedQuestions, getRefinedAnswers } from '../../../RefinerUtils/refinerDataSources';
import { questionMatcher, answerMatcher } from '../../../RefinerUtils/refinerMatchers';
import { formatStatus } from '../Utils/formatters';

const schema = [
    { field: "appointmentId", label: "Appointment ID", forDisplay: false },
    { field: "type", label: "Type" },
    { field: "description", label: "Description" },
    { field: "team", label: "Team" },
    { field: "code", label: "Code" },
    { field: "appointee.name", label: "Name" },
    { field: "appointee.ni", label: "NI" },
    { field: "appointmentStatus", label: "Appointment Status", format: formatStatus },
    { field: "placeOfEmployment", label: "Place of Employment" },
    { field: "placeOfEmploymentCode", label: "Place of Employment Code" },
    { field: "question", label: "Question", forDisplay: false },
    { field: "answer", label: "Answer", forDisplay: false }
];

const horizontalSchema = [
    { field: "appointmentId", label: "Appointment ID", forDisplay: false },
    { field: "type", label: "Type" },
    { field: "description", label: "Description" },
    { field: "team", label: "Team" },
    { field: "code", label: "Code" },
    { field: "appointee.name", label: "Name" },
    { field: "appointee.ni", label: "NI" },
    { field: "appointmentStatus", label: "Appointment Status", format: formatStatus },
    { field: "placeOfEmployment", label: "Place of Employment" },
    { field: "placeOfEmploymentCode", label: "Place of Employment Code" }

];

const refinerFields = [
    { name: 'appointmentStatus', label: "Appointment Status", type: 'list', format: formatStatus },
    { name: 'template', label: 'Template', type: 'list' },
    { name: 'readyForApproval', label: 'Ready for Approval', type: 'list' },
    { name: 'appointee.ni', label: 'NI', type: 'text' },
    { name: 'team', label: 'Team Name contains', type: 'text' },
    { name: 'question', label: 'Question', type: 'select', dataSource: getRefinedQuestions, matcher: questionMatcher },
    { name: 'answer', label: 'Answer', type: 'select', dataSource: getRefinedAnswers, matcher: answerMatcher }
];

function ResponseDataView({
    displayData,
    exportDataSource,
    filterOpen,
    isFiltering,
    grouping,
    refinerOpen,
    selected,
    showData,
    sourceData,
    title,
    year,
    onFilterClear,
    onFilterSubmit,
    onFilterToggle,
    onFilteringChange,
    onRefinerToggle,
    onRefinementChange,
    onSelectionChange,
    onYearChange,
    exportDataSourceHorizontal
}) {
    const getQuestionColumns = (data) => {
        let maxQuestions = 0;
        for (const row of data) {
            if (row.numberOfQuestions > maxQuestions) {
                maxQuestions = row.numberOfQuestions;
            }
        }
        const dynamicQuestions = [];
        if (maxQuestions > 0) {
            for (let i = 0; i < maxQuestions; i++) {
                const questionRow = { field: 'q'.concat(i + 1), label: 'Q'.concat(i + 1) };
                dynamicQuestions.push(questionRow);
                const answerRow = { field: 'a'.concat(i + 1), label: 'A'.concat(i + 1) };
                dynamicQuestions.push(answerRow);
            }
        }
        return dynamicQuestions;
    };

    return (
        <Page title={title}>
            <Section>
                <TeamTypeFilter
                    isFiltering={isFiltering}
                    open={filterOpen}
                    togglePanel={onFilterToggle}
                    grouping={grouping}
                    setIsFiltering={onFilteringChange}
                    year={year}
                    numberOfFutureYears={1}
                    updateYear={onYearChange}
                    onClear={onFilterClear}
                    onSubmit={onFilterSubmit}
                />
            </Section>

            <Section>
                <Refiner
                    disabled={isFiltering}
                    fields={refinerFields}
                    open={refinerOpen}
                    data={sourceData}
                    onRefinementChange={onRefinementChange}
                    onPanelToggleClick={onRefinerToggle}
                />
            </Section>

            {showData && !isFiltering && (
                <>
                    <Section>
                        <DataTable
                            dataSource={displayData}
                            expansion={ExpansionContent}
                            keyField="appointmentId"
                            schema={schema}
                            pagination
                            selected={selected}
                            selection
                            onSelectionChange={onSelectionChange}
                        />
                    </Section>

                    <Section variant="commands">
                        <Section.Command>
                            <ExportCsvButton
                                dataSource={exportDataSource}
                                fileName="appointee_response_data.csv"
                                includeHeaders={true}
                                schema={schema}
                                text="Export CSV ⬇️"
                            />
                        </Section.Command>
                        <Section.Command>
                            <ExportCsvButton
                                dataSource={exportDataSourceHorizontal}
                                fileName="appointee_response_data_horizontal.csv"
                                includeHeaders={true}
                                schema={horizontalSchema}
                                text="Export CSV ➡️"
                                dynamicColumnFunction={getQuestionColumns}
                            />
                        </Section.Command>
                    </Section>
                </>
            )}
        </Page>
    );
}

ResponseDataView.propTypes = {
    dataSource: PropTypes.oneOfType(dataSourceTypes),
    exportDataSource: PropTypes.oneOfType(dataSourceTypes),
    exportDataSourceHorizontal: PropTypes.oneOfType(dataSourceTypes),
    filterOpen: PropTypes.bool,
    grouping: PropTypes.string.isRequired,
    refinedData: PropTypes.array,
    refinerOpen: PropTypes.bool,
    selected: PropTypes.object,
    showData: PropTypes.bool,
    title: PropTypes.string.isRequired,
    year: PropTypes.string,
    onFilterClear: PropTypes.func.isRequired,
    onFilterSubmit: PropTypes.func.isRequired,
    onFilterToggle: PropTypes.func.isRequired,
    onRefinementChange: PropTypes.func.isRequired,
    onRefinerToggle: PropTypes.func.isRequired,
    onSelectionChange: PropTypes.func.isRequired,
    onYearChange: PropTypes.func.isRequired
};

export default React.memo(ResponseDataView);